import { useState } from "react";
import { filters } from "fabric";
import { Box, Typography, Slider, Button, Grid, Divider } from "@mui/material";
import { useData } from "../hooks/useData";
import useCanvasHistory from "../hooks/useCanvasHistory";

// Define the available filters and their properties
const filterDefaultValues = [
  {
    name: "Reset",
    property: "reset",
    position: -1,
    noSlider: true,
    value: 0,
    range: { min: 0, max: 100 },
    step: null,
    markInterval: null,
  },
  {
    name: "Grayscale",
    property: "grayscale",
    position: 0,
    noSlider: true,
    value: 0,
    range: { min: 0, max: 100 },
    step: null,
    markInterval: null,
  },
  {
    name: "Invert",
    property: "invert",
    position: 1,
    noSlider: true,
    value: 0,
    range: { min: 0, max: 100 },
    step: null,
    markInterval: null,
  },
  {
    name: "Sepia",
    property: "sepia",
    position: 2,
    noSlider: true,
    value: 0,
    range: { min: 0, max: 100 },
    step: null,
    markInterval: null,
  },
  {
    name: "Brightness",
    property: "brightness",
    position: 3,
    noSlider: false,
    value: 0,
    range: { min: -1, max: 1 },
    step: 0.01,
    markInterval: 0.2,
  },
  {
    name: "Contrast",
    property: "contrast",
    position: 4,
    noSlider: false,
    value: 0,
    range: { min: -1, max: 1 },
    step: 0.01,
    markInterval: 0.2,
  },
  {
    name: "Saturation",
    property: "saturation",
    position: 5,
    noSlider: false,
    value: 0,
    range: { min: -1, max: 1 },
    step: 0.01,
    markInterval: 0.2,
  },
  {
    name: "Noise",
    property: "noise",
    position: 6,
    noSlider: false,
    value: 0,
    range: { min: 0, max: 100 },
    step: 0.5,
    markInterval: 10,
  },
  {
    name: "Pixelate",
    property: "pixelate",
    position: 7,
    noSlider: false,
    value: 1,
    range: { min: 1, max: 20 },
    step: 0.5,
    markInterval: 2,
  },
  {
    name: "Hue",
    property: "hue",
    position: 8,
    noSlider: false,
    value: 0,
    range: { min: -2, max: 2 },
    step: 0.01,
    markInterval: 0.4,
  },
];

const ImageFilterToolbar = ({ canvas }) => {
  const { setSnackbar, selectedObject } = useData();
  const { saveHistory } = useCanvasHistory(canvas);
  const [filterValues, setFilterValues] = useState(filterDefaultValues);
  const [selectedFilter, setSelectedFilter] = useState(filterDefaultValues[0]);
  const [isFilterApplied, setIsFilterApplied] = useState({});

  // Apply a filter to the selected image
  const applyFilter = (position, filter) => {
    if (!selectedObject || selectedObject.type !== "image") {
      setSnackbar({
        open: true,
        message: "Please select an image to apply filters.",
        severity: "error",
      });
      return;
    }

    try {
      if (position === -1) {
        // Reset all filters
        selectedObject.filters = [];
        setIsFilterApplied({});
      } else {
        // Apply the specific filter
        selectedObject.filters[position] = filter;
        setIsFilterApplied((prev) => ({ ...prev, [position]: true }));
      }
      selectedObject.applyFilters();
      canvas.requestRenderAll();
      saveHistory(canvas);
    } catch (error) {
      console.error("ImageFilterToolbar: Failed to apply filter", error);
      setSnackbar({
        open: true,
        message: "Error applying filter. Please try again.",
        severity: "error",
      });
    }
  };

  // Handle filter application based on the selected filter
  const handleApplyFilter = (updatedFilter) => {
    const { position } = updatedFilter;
    switch (position) {
      case -1:
        applyFilter(-1, null); // Reset filters
        break;
      case 0:
        applyFilter(position, new filters.Grayscale());
        break;
      case 1:
        applyFilter(position, new filters.Invert());
        break;
      case 2:
        applyFilter(position, new filters.Sepia());
        break;
      case 3:
        applyFilter(
          position,
          new filters.Brightness({
            brightness: updatedFilter.value,
          })
        );
        break;
      case 4:
        applyFilter(
          position,
          new filters.Contrast({ contrast: updatedFilter.value })
        );
        break;
      case 5:
        applyFilter(
          position,
          new filters.Saturation({
            saturation: updatedFilter.value,
          })
        );
        break;
      case 6:
        applyFilter(
          position,
          new filters.Noise({ noise: updatedFilter.value })
        );
        break;
      case 7:
        applyFilter(
          position,
          new filters.Pixelate({ blocksize: updatedFilter.value })
        );
        break;
      case 8:
        applyFilter(
          position,
          new filters.HueRotation({
            rotation: updatedFilter.value,
          })
        );
        break;
      default:
        break;
    }
  };

  // Handle slider value change
  const handleSliderChange = (event, newValue) => {
    const updatedFilter = { ...selectedFilter, value: newValue };
    setSelectedFilter(updatedFilter);
    setFilterValues((prev) =>
      prev.map((filter) =>
        filter.position === updatedFilter.position ? updatedFilter : filter
      )
    );
    handleApplyFilter(updatedFilter);
  };

  // Handle filter button click
  const handleFilterClick = (filter) => {
    setSelectedFilter(filter);
    handleApplyFilter(filter);
  };

  // Generate marks for the slider based on the filter's range and markInterval
  const generateMarks = (filter) => {
    if (filter.noSlider || !filter.markInterval) return [];
    const marks = [];
    const { min, max } = filter.range;
    const interval = filter.markInterval;

    for (let value = min; value <= max; value += interval) {
      marks.push({
        value,
        label: value.toFixed(filter.step < 1 ? 1 : 0), // Show 1 decimal place for small steps
      });
    }
    return marks;
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          color: "#0d2445",
          fontFamily: "Roboto",
          fontWeight: 700,
          textAlign: "left",
        }}
      >
        Image Filters
      </Typography>
      <Typography
        variant="body1"
        sx={{
          marginTop: "8px",
          color: "#5e5e5e",
          textAlign: "left",
        }}
      >
        Apply filters to the selected image. Use the slider to adjust filter
        intensity where available. Click "Reset" to remove all filters.
      </Typography>
      <Divider
        sx={{
          marginTop: "12px",
          marginBottom: "12px",
          borderWidth: 1,
          borderColor: "grey",
          borderLeft: 0,
          borderRight: 0,
          borderBottom: 0,
        }}
        variant="fullWidth"
        orientation="horizontal"
      />
      <Box>
        {selectedFilter.noSlider ? null : (
          <Slider
            value={selectedFilter.value}
            min={selectedFilter.range.min}
            max={selectedFilter.range.max}
            step={selectedFilter.step}
            marks={generateMarks(selectedFilter)}
            valueLabelDisplay="auto"
            onChange={handleSliderChange}
            sx={{
              marginTop: "16px",
              marginBottom: "48px",
              "& .MuiSlider-markLabel": {
                marginBottom: "4px", // Added margin-bottom to mark labels
              },
            }}
          />
        )}
        <Grid container spacing={2} justifyContent="space-evenly">
          {filterValues.map((filter) => (
            <Grid item xs={4} key={filter.position}>
              <Button
                variant="contained"
                onClick={() => handleFilterClick(filter)}
                sx={{
                  height: 35,
                  width: 88,
                  backgroundColor:
                    filter.position === -1 &&
                    Object.keys(isFilterApplied).length === 0
                      ? "grey"
                      : isFilterApplied[filter.position]
                      ? "#3A54A4"
                      : "#0d2445",
                  "&:hover": {
                    backgroundColor: isFilterApplied[filter.position]
                      ? "#2A3E7A"
                      : "#0A1A35",
                  },
                }}
                disabled={
                  filter.position === -1 &&
                  Object.keys(isFilterApplied).length === 0
                }
              >
                <Typography
                  sx={{
                    color: "white",
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: "12px",
                    textAlign: "center",
                  }}
                >
                  {filter.name}
                </Typography>
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default ImageFilterToolbar;
